import { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CandleType, YAxisType } from 'klinecharts';
import { useChartStore } from '@/store/chartStore';
import { trackTimeframeChange } from '@/services/gtag';
import { IndicatorModal } from './IndicatorModal';
import { hexToRgba } from '@/utils/color';
import type { Timeframe } from '@/types';
import { ADJUSTMENT_ITEMS } from '@/components/Header/adjustmentItems';
import {
  ClockIcon,
  IndicatorIcon,
  TrendLineIcon,
} from '@/components/icons';

type PriceScaleType = 'normal' | 'log' | 'percentage';

const SCALE_OPTIONS: { value: PriceScaleType; label: string; title: string }[] = [
  { value: 'normal',     label: 'Reg.', title: 'Linear price axis' },
  { value: 'log',        label: 'Log',  title: 'Logarithmic price axis' },
  { value: 'percentage', label: 'Pct',  title: 'Percentage axis vs first visible bar close' },
];

interface DropdownPos {
  bottom: number;
  right: number;
  width: number;
}

function useDropdownPosition() {
  const [pos, setPos] = useState<DropdownPos | null>(null);
  const measure = (ref: React.RefObject<HTMLElement | null>, width: number) => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      setPos({
        bottom: window.innerHeight - rect.top + 6,
        right: window.innerWidth - rect.right,
        width,
      });
    }
  };
  return { pos, measure, clear: () => setPos(null) };
}

/* ── Period logic (mirrors PeriodBar) ─────────────────────────────────────── */

const PERIODS = ['1D', '5D', '1M', '3M', '6M', 'YTD', '1Y', '5Y', 'All'] as const;
type Period = (typeof PERIODS)[number];

const PERIOD_TIMEFRAME: Record<Period, Timeframe> = {
  '1D':  '1m',
  '5D':  '5m',
  '1M':  '1D',
  '3M':  '1D',
  '6M':  '1D',
  'YTD': '1D',
  '1Y':  '1W',
  '5Y':  '1M',
  'All': '1W',
};

function periodToFromDate(period: Period): number | null {
  if (period === 'All') return null;
  const now = new Date();
  const y = now.getUTCFullYear(), mo = now.getUTCMonth(), d = now.getUTCDate();
  let ts: number;
  switch (period) {
    case '1D':  ts = Date.UTC(y, mo, d - 1);  break;
    case '5D':  ts = Date.UTC(y, mo, d - 5);  break;
    case '1M':  ts = Date.UTC(y, mo - 1, d);  break;
    case '3M':  ts = Date.UTC(y, mo - 3, d);  break;
    case '6M':  ts = Date.UTC(y, mo - 6, d);  break;
    case 'YTD': ts = Date.UTC(y, 0, 1);        break;
    case '1Y':  ts = Date.UTC(y - 1, mo, d);  break;
    case '5Y':  ts = Date.UTC(y - 5, mo, d);  break;
    default:    return null;
  }
  return Math.floor(ts / 1000);
}

/* ── Timeframe options ────────────────────────────────────────────────────── */

interface TFOption {
  label: string;
  long: string;
  value: Timeframe;
}

const INTRADAY_TIMEFRAMES: TFOption[] = [
  { label: '1m',  long: '1 Minute',   value: '1m'  },
  { label: '5m',  long: '5 Minutes',  value: '5m'  },
  { label: '15m', long: '15 Minutes', value: '15m' },
  { label: '30m', long: '30 Minutes', value: '30m' },
  { label: '1h',  long: '1 Hour',     value: '1h'  },
];

const DAILY_TIMEFRAMES: TFOption[] = [
  { label: '1D', long: '1 Day',   value: '1D' },
  { label: '1W', long: '1 Week',  value: '1W' },
  { label: '1M', long: '1 Month', value: '1M' },
];

const ALL_TIMEFRAMES = [...INTRADAY_TIMEFRAMES, ...DAILY_TIMEFRAMES];

/* ── Candle type icons & options ──────────────────────────────────────────── */

type CandleStyle = CandleType | 'hlc';

const DEFAULT_NO_CHANGE_COLOR = '#9598a1';

function CandleSolidIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="2" x2="6" y2="5" />
      <rect x="3.5" y="5" width="5" height="9" rx="0.5" fill="currentColor" stroke="none" />
      <line x1="6" y1="14" x2="6" y2="17" />
      <line x1="16" y1="5" x2="16" y2="8" />
      <rect x="13.5" y="8" width="5" height="9" rx="0.5" fill="currentColor" stroke="none" opacity="0.45" />
      <line x1="16" y1="17" x2="16" y2="20" />
    </svg>
  );
}

function CandleHollowIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="2" x2="6" y2="5" />
      <rect x="3.5" y="5" width="5" height="9" rx="0.5" />
      <line x1="6" y1="14" x2="6" y2="17" />
      <line x1="16" y1="5" x2="16" y2="8" />
      <rect x="13.5" y="8" width="5" height="9" rx="0.5" opacity="0.45" />
      <line x1="16" y1="17" x2="16" y2="20" />
    </svg>
  );
}

function OhlcIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="4" x2="6" y2="17" />
      <line x1="3.5" y1="10" x2="6" y2="10" />
      <line x1="6" y1="7" x2="8.5" y2="7" />
      <line x1="16" y1="6" x2="16" y2="19" />
      <line x1="13.5" y1="9" x2="16" y2="9" />
      <line x1="16" y1="14" x2="18.5" y2="14" />
    </svg>
  );
}

function AreaIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="2 17 7 9 12 13 17 5 22 10" strokeWidth="1.75" />
      <path d="M2 17 L7 9 L12 13 L17 5 L22 10 L22 20 L2 20 Z" fill="currentColor" opacity="0.18" stroke="none" />
    </svg>
  );
}

function HlcIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="4" x2="6" y2="17" />
      <line x1="6" y1="9" x2="9.5" y2="9" />
      <line x1="16" y1="6" x2="16" y2="20" />
      <line x1="16" y1="14" x2="19.5" y2="14" />
    </svg>
  );
}

interface CandleTypeItem {
  value: CandleStyle;
  label: string;
  Icon: ({ size }: { size?: number }) => React.JSX.Element;
}

const CANDLE_TYPES: CandleTypeItem[] = [
  { value: CandleType.CandleSolid,  label: 'Candles',   Icon: CandleSolidIcon  },
  { value: CandleType.CandleStroke, label: 'Hollow',    Icon: CandleHollowIcon },
  { value: CandleType.Ohlc,         label: 'OHLC Bars', Icon: OhlcIcon         },
  { value: CandleType.Area,         label: 'Area',      Icon: AreaIcon         },
  { value: 'hlc',                   label: 'HLC Bars',  Icon: HlcIcon          },
];

/* ── Component ────────────────────────────────────────────────────────────── */

export function ChartActionBar() {
  const chart                = useChartStore((s) => s.chart);
  const chartPeriod          = useChartStore((s) => s.chartPeriod);
  const setChartPeriod       = useChartStore((s) => s.setChartPeriod);

  const setChartFromDate     = useChartStore((s) => s.setChartFromDate);
  const setTimeframe         = useChartStore((s) => s.setTimeframe);
  const timeframe            = useChartStore((s) => s.timeframe);
  const setActiveCandleType  = useChartStore((s) => s.setActiveCandleType);
  const drawerCollapsed      = useChartStore((s) => s.drawingToolbarCollapsed);
  const setDrawerCollapsed   = useChartStore((s) => s.setDrawingToolbarCollapsed);
  const priceScaleType       = useChartStore((s) => s.priceScaleType);
  const setPriceScaleType    = useChartStore((s) => s.setPriceScaleType);
  const priceAdjustments     = useChartStore((s) => s.priceAdjustments);
  const setPriceAdjustments  = useChartStore((s) => s.setPriceAdjustments);

  const [indicatorOpen, setIndicatorOpen] = useState(false);
  const [tfOpen, setTfOpen] = useState(false);
  const [candleOpen, setCandleOpen] = useState(false);
  const [scaleOpen, setScaleOpen] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);

  const tfRef = useRef<HTMLDivElement>(null);
  const candleRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const adjRef = useRef<HTMLDivElement>(null);
  const tfMenuRef = useRef<HTMLDivElement>(null);
  const candleMenuRef = useRef<HTMLDivElement>(null);
  const scaleMenuRef = useRef<HTMLDivElement>(null);
  const adjMenuRef = useRef<HTMLDivElement>(null);

  const tfPos = useDropdownPosition();
  const candlePos = useDropdownPosition();
  const scalePos = useDropdownPosition();
  const adjPos = useDropdownPosition();

  function toggleAdjustment(value: (typeof ADJUSTMENT_ITEMS)[number]['value']) {
    const next = priceAdjustments.includes(value)
      ? priceAdjustments.filter((v) => v !== value)
      : [...priceAdjustments, value];
    setPriceAdjustments(next);
  }

  // Close dropdowns on outside click
  // Menus are portaled to document.body, so they're excluded from tfRef/candleRef/scaleRef/adjRef's
  // DOM subtree — check the portaled menu refs too, or a tap inside the menu closes it on
  // pointerdown before the item's own onClick can fire.
  useEffect(() => {
    if (!tfOpen && !candleOpen && !scaleOpen && !adjOpen) return;
    function handler(e: PointerEvent) {
      const t = e.target as Node;
      if (tfRef.current && !tfRef.current.contains(t) && !tfMenuRef.current?.contains(t)) setTfOpen(false);
      if (candleRef.current && !candleRef.current.contains(t) && !candleMenuRef.current?.contains(t)) setCandleOpen(false);
      if (scaleRef.current && !scaleRef.current.contains(t) && !scaleMenuRef.current?.contains(t)) setScaleOpen(false);
      if (adjRef.current && !adjRef.current.contains(t) && !adjMenuRef.current?.contains(t)) setAdjOpen(false);
    }
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [tfOpen, candleOpen, scaleOpen, adjOpen]);

  // Close dropdowns on resize/orientation change
  useEffect(() => {
    if (!tfOpen && !candleOpen && !scaleOpen && !adjOpen) return;
    function handleResize() {
      setTfOpen(false);
      setCandleOpen(false);
      setScaleOpen(false);
      setAdjOpen(false);
    }
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [tfOpen, candleOpen, scaleOpen, adjOpen]);

  function handlePeriodClick(period: Period) {
    setChartPeriod(period);
    setChartFromDate(periodToFromDate(period));
    setTimeframe(PERIOD_TIMEFRAME[period]);
  }

  function handleTimeframeSelect(tf: Timeframe) {
    setTimeframe(tf);
    trackTimeframeChange(tf);
    setTfOpen(false);
  }

  function applyCandleType(type: CandleStyle) {
    setActiveCandleType(type);
    setCandleOpen(false);
    const hlcUp = useChartStore.getState().hlcUpColor;
    const hlcDown = useChartStore.getState().hlcDownColor;
    if (type === 'hlc') {
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({
        candle: { type: CandleType.Area, area: { lineColor: 'rgba(0,0,0,0)', backgroundColor: 'rgba(0,0,0,0)' } },
      });
      (chart as unknown as { createIndicator: (n: string, s: boolean, o: { id: string }) => void })
        ?.createIndicator('HLC', true, { id: 'candle_pane' });
      chart?.setStyles({ indicator: { ohlc: { upColor: hlcUp, downColor: hlcDown, noChangeColor: DEFAULT_NO_CHANGE_COLOR } } });
    } else if (type === CandleType.Area) {
      const up = useChartStore.getState().candleUpColor;
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({
        candle: { type: CandleType.Area, area: { lineColor: up, backgroundColor: hexToRgba(up, 0.15) } },
      });
    } else {
      chart?.removeIndicator('candle_pane', 'HLC');
      chart?.setStyles({ candle: { type: type as CandleType } });
    }
  }

  // Derive current candle type from chart state
  const activeCandleType = useMemo<CandleStyle>(() => {
    if (!chart) return CandleType.CandleSolid;
    const hasHLC = chart.getIndicatorByPaneId('candle_pane', 'HLC');
    if (hasHLC) return 'hlc';
    return (chart.getStyles()?.candle?.type as CandleStyle) ?? CandleType.CandleSolid;
  }, [chart]);

  const activeTF = ALL_TIMEFRAMES.find((tf) => tf.value === timeframe);
  const activeCandle = CANDLE_TYPES.find((c) => c.value === activeCandleType) ?? CANDLE_TYPES[0];

  return (
    <>
      <div
        className="h-12 border-t flex items-center gap-1 px-2 md:hidden"
        style={{
          background: 'var(--glass-header)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTopColor: 'var(--glass-border)',
          boxShadow: 'var(--glass-shadow)',
        }}
      >
        {/* Period pills — scrollable */}
        <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-none flex-1 min-w-0">
          {PERIODS.map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodClick(p)}
              className={`px-2 py-0.5 rounded text-[11px] font-medium transition-colors shrink-0 ${
                chartPeriod === p
                  ? 'bg-accent/20 text-accent'
                  : 'text-muted hover:text-text hover:bg-white/[0.06]'
              }`}
            >
              {p}
            </button>
          ))}
        </div>

        <div className="w-px h-4 shrink-0" style={{ background: 'var(--glass-border)' }} />

        {/* Timeframe dropdown */}
        <div className="relative shrink-0" ref={tfRef}>
          <button
            onClick={() => {
              if (!tfOpen) tfPos.measure(tfRef, 160);
              setTfOpen((v) => !v);
            }}
            title={`Timeframe: ${activeTF?.long ?? timeframe}`}
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors ${
              tfOpen ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            <ClockIcon size={14} />
            <span className="font-medium">{activeTF?.label ?? timeframe}</span>
          </button>

          {tfOpen && tfPos.pos && createPortal(
            <div
              ref={tfMenuRef}
              className="glass-modal rounded-xl z-modal-elevated py-1.5 animate-menu-enter"
              style={{
                position: 'fixed',
                bottom: `${tfPos.pos.bottom}px`,
                right: `${tfPos.pos.right}px`,
                minWidth: `${tfPos.pos.width}px`,
              }}
            >
              <p className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-modal-muted">Intraday</p>
              {INTRADAY_TIMEFRAMES.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => handleTimeframeSelect(tf.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-4 ${
                    timeframe === tf.value ? 'text-accent bg-accent/10' : 'text-modal-text hover:bg-modal-hover'
                  }`}
                >
                  <span>{tf.long}</span>
                  <span className="text-xs text-modal-muted font-mono">{tf.label}</span>
                </button>
              ))}
              <div className="h-px bg-border mx-2 my-1" />
              <p className="px-3 pt-0.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-modal-muted">Daily</p>
              {DAILY_TIMEFRAMES.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => handleTimeframeSelect(tf.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between gap-4 ${
                    timeframe === tf.value ? 'text-accent bg-accent/10' : 'text-modal-text hover:bg-modal-hover'
                  }`}
                >
                  <span>{tf.long}</span>
                  <span className="text-xs text-modal-muted font-mono">{tf.label}</span>
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>

        {/* Candle type dropdown */}
        <div className="relative shrink-0" ref={candleRef}>
          <button
            onClick={() => {
              if (!candleOpen) candlePos.measure(candleRef, 176);
              setCandleOpen((v) => !v);
            }}
            title={activeCandle.label}
            className={`flex items-center justify-center p-1.5 rounded transition-colors ${
              candleOpen ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            <activeCandle.Icon size={18} />
          </button>

          {candleOpen && candlePos.pos && createPortal(
            <div
              ref={candleMenuRef}
              className="glass-modal rounded-xl z-modal-elevated py-1.5 animate-menu-enter"
              style={{
                position: 'fixed',
                bottom: `${candlePos.pos.bottom}px`,
                right: `${candlePos.pos.right}px`,
                width: `${candlePos.pos.width}px`,
              }}
            >
              {CANDLE_TYPES.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  onClick={() => applyCandleType(value)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    activeCandleType === value
                      ? 'text-accent bg-accent/10'
                      : 'text-modal-muted hover:text-modal-text hover:bg-modal-hover'
                  }`}
                >
                  <Icon size={18} />
                  <span className={activeCandleType === value ? 'font-medium' : ''}>{label}</span>
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>

        <div className="w-px h-4 shrink-0" style={{ background: 'var(--glass-border)' }} />

        {/* Indicators */}
        <button
          onClick={() => setIndicatorOpen((v) => !v)}
          title="Indicators"
          className={`flex items-center justify-center p-1.5 rounded transition-colors shrink-0 ${
            indicatorOpen ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <IndicatorIcon size={18} />
        </button>

        {/* Draw */}
        <button
          onClick={() => {
            setDrawerCollapsed(!drawerCollapsed);

          }}
          title={drawerCollapsed ? 'Show Drawing Tools' : 'Hide Drawing Tools'}
          className={`flex items-center justify-center p-1.5 rounded transition-colors shrink-0 ${
            !drawerCollapsed ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
          }`}
        >
          <TrendLineIcon size={18} />
        </button>

        <div className="w-px h-4 shrink-0" style={{ background: 'var(--glass-border)' }} />

        {/* Scale type dropdown */}
        <div className="relative shrink-0" ref={scaleRef}>
          <button
            onClick={() => {
              if (!scaleOpen) scalePos.measure(scaleRef, 112);
              setScaleOpen((v) => !v);
            }}
            title="Price axis scale"
            className={`flex items-center justify-center p-1.5 rounded transition-colors ${
              scaleOpen ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            <span className="text-[11px] font-medium">
              {SCALE_OPTIONS.find((o) => o.value === priceScaleType)?.label ?? 'Reg.'}
            </span>
          </button>

          {scaleOpen && scalePos.pos && createPortal(
            <div
              ref={scaleMenuRef}
              className="glass-modal rounded-xl z-modal-elevated py-1 animate-menu-enter"
              style={{
                position: 'fixed',
                bottom: `${scalePos.pos.bottom}px`,
                right: `${scalePos.pos.right}px`,
                width: `${scalePos.pos.width}px`,
              }}
            >
              {SCALE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  title={opt.title}
                  onClick={() => {
                    setPriceScaleType(opt.value);
                    setScaleOpen(false);
                    const type =
                      opt.value === 'log'        ? YAxisType.Log
                      : opt.value === 'percentage' ? YAxisType.Percentage
                      : YAxisType.Normal;
                    chart?.setStyles({ yAxis: { type } } as never);
                    chart?.resize();
                  }}
                  className={`w-full text-left px-3 py-1.5 text-sm transition-colors ${
                    priceScaleType === opt.value
                      ? 'text-accent bg-accent/10'
                      : 'text-modal-muted hover:text-modal-text hover:bg-modal-hover'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>

        {/* Price adjustment (splits / dividends / scrip dividends / rights) */}
        <div className="relative shrink-0" ref={adjRef}>
          <button
            onClick={() => {
              if (!adjOpen) adjPos.measure(adjRef, 208);
              setAdjOpen((v) => !v);
            }}
            title="Adjust for corporate actions"
            className={`flex items-center justify-center p-1.5 rounded text-[11px] font-medium transition-colors ${
              adjOpen ? 'bg-accent/20 text-accent' : 'text-muted hover:text-text hover:bg-panel-hover'
            }`}
          >
            Adj.
          </button>

          {adjOpen && adjPos.pos && createPortal(
            <div
              ref={adjMenuRef}
              className="glass-modal rounded-xl z-modal-elevated py-1.5 animate-menu-enter"
              style={{
                position: 'fixed',
                bottom: `${adjPos.pos.bottom}px`,
                right: `${adjPos.pos.right}px`,
                width: `${adjPos.pos.width}px`,
              }}
            >
              <p className="px-3 pb-1.5 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-modal-muted">Adjust for</p>
              {ADJUSTMENT_ITEMS.map(({ value, label, note }) => {
                const checked = priceAdjustments.includes(value);
                return (
                  <label
                    key={value}
                    title={note}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-modal-hover"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleAdjustment(value)}
                      className="rounded border-border accent-[var(--color-accent)] h-4 w-4 cursor-pointer"
                    />
                    <span className={checked ? 'text-modal-text font-medium' : 'text-modal-muted'}>{label}</span>
                    {note && (
                      <span className="text-[10px] uppercase tracking-wide text-muted/70 ml-auto">approx</span>
                    )}
                  </label>
                );
              })}
            </div>,
            document.body
          )}
        </div>
      </div>

      {indicatorOpen && <IndicatorModal onClose={() => setIndicatorOpen(false)} />}
    </>
  );
}
